import React, { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import CorporateEventsService, { CreateInvitedUserParams, InvitedUser } from '~/services/CorporateEventsService';

import ImportInvitedUsersDialog from './ImportInvitedUsersDialog';
import InvitedUserDialog from './InvitedUserDialog';
import InvitedUsersList from './InvitedUsersList';

interface InvitedUsersProps {
  eventId?: string;
  invitedUsers: Array<InvitedUser>;
  onInvitedUsersChange: (invitedUsers: Array<InvitedUser>) => void;
}

export default function InvitedUsers({ eventId, invitedUsers, onInvitedUsersChange }: InvitedUsersProps) {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<InvitedUser | null>(null);

  const createMutation = useMutation({
    mutationFn: ({ eventId, user }: { eventId: string; user: CreateInvitedUserParams }) =>
      CorporateEventsService.createInvitedUser(eventId, user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'invited-users', eventId] });
      enqueueSnackbar('Invited user created successfully', { variant: 'success' });
      setUserDialogOpen(false);
      setUserToEdit(null);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error creating invited user: ${error.message}`, { variant: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, user }: { id: string; user: Partial<CreateInvitedUserParams> }) =>
      CorporateEventsService.updateInvitedUser(id, user),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'invited-users', eventId] });
      enqueueSnackbar('Invited user updated successfully', { variant: 'success' });
      setUserDialogOpen(false);
      setUserToEdit(null);
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error updating invited user: ${error.message}`, { variant: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ eventId, emails }: { eventId: string; emails: Array<string> }) =>
      CorporateEventsService.removeInvitedUsers(eventId, emails),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['corporate-events', 'invited-users', eventId] });
      enqueueSnackbar('Invited users removed successfully', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Error removing invited users: ${error.message}`, { variant: 'error' });
    },
  });

  const handleSaveUser = async (user: InvitedUser) => {
    if (!eventId) {
      // For create mode, just update local state
      const existingIndex = invitedUsers.findIndex((u) => u.email === user.email);
      let updatedUsers: Array<InvitedUser>;
      if (existingIndex >= 0) {
        updatedUsers = [...invitedUsers];
        updatedUsers[existingIndex] = user;
      } else {
        updatedUsers = [...invitedUsers, user];
      }
      onInvitedUsersChange(updatedUsers);
      enqueueSnackbar(`User ${existingIndex >= 0 ? 'updated' : 'added'}. It will be saved when you save the event.`, {
        variant: 'success',
      });
      setUserDialogOpen(false);
      setUserToEdit(null);
      return;
    }

    const userData: CreateInvitedUserParams = {
      fullName: user.fullName,
      email: user.email,
      role: user.role,
      notes: user.notes,
      hasAnalyticsAccess: user.hasAnalyticsAccess,
    };

    if (user.id) {
      await updateMutation.mutateAsync({
        id: user.id,
        user: userData,
      });
    } else {
      await createMutation.mutateAsync({
        eventId,
        user: userData,
      });
    }
  };

  const handleDeleteUsers = async (emails: Array<string>) => {
    if (!eventId) {
      const updatedUsers = invitedUsers.filter((u) => !emails.includes(u.email));
      onInvitedUsersChange(updatedUsers);
      enqueueSnackbar('Users removed. They will be removed when you save the event.', {
        variant: 'success',
      });
      return;
    }

    await deleteMutation.mutateAsync({
      eventId,
      emails,
    });
  };

  const handleCreateClick = () => {
    setUserToEdit(null);
    setUserDialogOpen(true);
  };

  const handleEditClick = (user: InvitedUser) => {
    setUserToEdit(user);
    setUserDialogOpen(true);
  };

  const handleClose = () => {
    setUserDialogOpen(false);
    setUserToEdit(null);
  };

  const handleImportClick = () => {
    setImportDialogOpen(true);
  };

  const handleImportClose = () => {
    setImportDialogOpen(false);
  };

  return (
    <>
      <InvitedUsersList
        eventId={eventId}
        invitedUsers={invitedUsers}
        onEditClick={handleEditClick}
        onCreateClick={handleCreateClick}
        onDeleteClick={handleDeleteUsers}
        onImportClick={handleImportClick}
      />
      <InvitedUserDialog
        open={userDialogOpen}
        invitedUser={userToEdit}
        onClose={handleClose}
        onSave={handleSaveUser}
        isSaving={createMutation.isPending || updateMutation.isPending}
      />
      {eventId && <ImportInvitedUsersDialog open={importDialogOpen} eventId={eventId} onClose={handleImportClose} />}
    </>
  );
}
